/**
 * Procedural geometry for the City and Interior levels, as plain data (no
 * three.js; unit-tested in Node). Everything is a list of parts:
 *
 *   { shape: "box" | "cyl", x, y, z, w, h, d, color, glow? }
 *
 * x/z on the ground, y up, (x, y, z) the centre of the part's base, sizes in
 * world units (1 unit ~ 4 m). scene code draws every part of one shape with
 * a single InstancedMesh, so a whole district is a handful of draw calls.
 *
 * What the sizes mean (shown in the legend, never decoration only):
 * - your HQ: floors = 3 + headcount / 25 (max 40)
 * - a rival's tower: height = market share (the leader is 30 floors)
 * - your sites: one per factory, otherwise one per product (at least one)
 * - desks inside HQ: one per person, or one per N people above 1,200
 * Filler buildings around the edge are scenery and say so in the legend.
 */

const FLOOR = 0.9; // world units per floor

const PALETTE = {
  you: 0x7ee8fa, youDark: 0x2b6f86, glass: 0x9fb4ff, rival: 0xfb7185, rivalDark: 0x8a3a4f,
  civic: 0xd8d0bd, civicDark: 0x8a7f68, bank: 0xe5b44f, filler: 0x3a4466, fillerLight: 0x56618a,
  ground: 0x0d1120, road: 0x161b2e, plaza: 0x1a2340, roof: 0x232a44, green: 0x2f6f5a, stack: 0x8b93ad,
};

/** Small deterministic RNG (mulberry32) from a string seed. */
function rngFrom(seedText) {
  let h = 1779033703 ^ String(seedText).length;
  for (let i = 0; i < String(seedText).length; i++) {
    h = Math.imul(h ^ String(seedText).charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  let a = h >>> 0;
  return () => {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const box = (x, y, z, w, h, d, color, extra) => ({ shape: "box", x, y, z, w, h, d, color, ...extra });
const cyl = (x, y, z, w, h, color, extra) => ({ shape: "cyl", x, y, z, w, h, d: w, color, ...extra });

const hqFloors = (headcount) => Math.max(3, Math.min(40, 3 + Math.round((headcount || 0) / 25)));
const rivalFloors = (share, maxShare) => Math.max(4, Math.round(30 * (share || 0) / Math.max(1e-6, maxShare || 1)));

/**
 * One building, as parts around (0, 0). kind decides the silhouette;
 * floors the height; tone "you" / "rival" / "civic" / "filler" the colours.
 */
function building(kind, floors, tone, rng) {
  const H = floors * FLOOR;
  const c = {
    you: [PALETTE.you, PALETTE.youDark], rival: [PALETTE.rival, PALETTE.rivalDark],
    civic: [PALETTE.civic, PALETTE.civicDark], filler: [PALETTE.filler, PALETTE.fillerLight],
  }[tone] || [PALETTE.filler, PALETTE.fillerLight];
  const parts = [];
  switch (kind) {
    case "tower": {
      // setback tiers + a crown; window bands every few floors
      const tiers = H > 14 ? 3 : H > 7 ? 2 : 1;
      let y = 0, w = 3.2;
      for (let t = 0; t < tiers; t++) {
        const th = (H / tiers) * (t === 0 ? 1.1 : 0.95);
        parts.push(box(0, y, 0, w, th, w, c[t % 2 ? 1 : 0]));
        for (let fy = y + 1.6; fy < y + th - 0.4; fy += 2.7) parts.push(box(0, fy, 0, w + 0.06, 0.18, w + 0.06, c[1]));
        y += th; w *= 0.78;
      }
      parts.push(box(0, y, 0, w * 0.6, 0.6, w * 0.6, c[1]));
      parts.push(cyl(0, y + 0.6, 0, 0.12, 1.6 + rng() * 1.2, PALETTE.stack));
      break;
    }
    case "factory": {
      parts.push(box(0, 0, 0, 6, 2.2, 4, c[1]));
      for (let i = 0; i < 4; i++) parts.push(box(-2.25 + i * 1.5, 2.2, 0, 1.2, 0.8, 4, c[0])); // sawtooth roof
      parts.push(cyl(2.2, 0, -1.4, 0.5, 4.5 + rng() * 2, PALETTE.stack));
      parts.push(cyl(1.2, 0, -1.4, 0.4, 3.5 + rng() * 2, PALETTE.stack));
      break;
    }
    case "datacenter": {
      parts.push(box(0, 0, 0, 5.5, 1.8, 4, c[1]));
      for (let i = 0; i < 3; i++) for (let j = 0; j < 2; j++) parts.push(box(-1.8 + i * 1.8, 1.8, -0.9 + j * 1.8, 1.1, 0.5, 1.1, c[0]));
      break;
    }
    case "store": {
      parts.push(box(0, 0, 0, 4.5, 1.6, 3.5, c[1]));
      parts.push(box(0, 1.1, 1.9, 4.5, 0.15, 0.6, c[0])); // awning
      parts.push(box(0, 1.6, 0, 3.6, 0.5, 2.6, PALETTE.roof));
      break;
    }
    case "warehouse": {
      parts.push(box(0, 0, 0, 6.5, 2.0, 4.5, c[1]));
      parts.push(box(0, 2.0, 0, 6.5, 0.3, 4.5, c[0]));
      for (let i = 0; i < 3; i++) parts.push(box(-2 + i * 2, 0, 2.3, 1.2, 1.3, 0.1, PALETTE.roof)); // loading doors
      break;
    }
    case "rig": {
      parts.push(box(0, 0, 0, 4, 0.6, 4, c[1]));
      for (const [x, z] of [[-1.5, -1.5], [1.5, -1.5], [-1.5, 1.5], [1.5, 1.5]]) parts.push(cyl(x, 0.6, z, 0.25, 3, PALETTE.stack));
      parts.push(box(0, 3.6, 0, 3.6, 0.5, 3.6, c[0]));
      parts.push(box(0.8, 4.1, 0, 0.6, 4 + rng() * 2, 0.6, c[0])); // derrick
      for (let i = 0; i < 2; i++) parts.push(cyl(-1.2 + i * 1.2, 4.1, -1, 0.7, 1.4, PALETTE.civic)); // tanks
      break;
    }
    case "campus": {
      parts.push(box(0, 0, 0, 6, 2.7, 2, c[0]));
      parts.push(box(0, 0, 0, 2, 3.6, 6, c[1]));
      parts.push(cyl(0, 3.6, 0, 1.0, 0.1, PALETTE.civic)); // helipad
      break;
    }
    case "fab": { // cleanroom block with roof fans and exhaust stacks
      parts.push(box(0, 0, 0, 6.2, 2.6, 4.4, PALETTE.civic));
      parts.push(box(0, 2.6, 0, 5.6, 0.25, 3.8, c[0]));
      for (let i = 0; i < 4; i++) parts.push(cyl(-2.1 + i * 1.4, 2.85, 0.8, 0.7, 0.25, PALETTE.stack));
      parts.push(cyl(2.4, 0, -1.6, 0.35, 5.5, PALETTE.stack));
      parts.push(cyl(1.6, 0, -1.6, 0.35, 5.0, PALETTE.stack));
      break;
    }
    case "mine": { // terraced pit with a spoil heap
      for (let t = 0; t < 4; t++) parts.push(box(0, -0.15 - t * 0.02, 0, 7 - t * 1.5, 0.12, 6 - t * 1.4, t % 2 ? 0x6b5a45 : 0x806a52));
      parts.push(box(-3.4, 0, 2.4, 2.2, 1.6, 1.6, 0x8a7a63));
      parts.push(box(2.6, 0, -2.4, 1.6, 0.9, 1.0, c[0]));
      break;
    }
    case "farm": { // crop strips, barn and silo
      for (let i = 0; i < 6; i++) parts.push(box(-2.5 + i, 0, 0.6, 0.7, 0.18, 4.4, i % 2 ? 0x3f8a52 : 0x5aa35f));
      parts.push(box(-2.6, 0, -2.7, 2.2, 1.6, 1.6, 0x9b3d3d));
      parts.push(cyl(2.6, 0, -2.6, 1.0, 3.4, PALETTE.civic));
      break;
    }
    case "solar": { // panel rows on frames
      for (let r = 0; r < 4; r++) for (let k = 0; k < 3; k++) {
        parts.push(box(-2.4 + k * 2.4, 0, -2.1 + r * 1.4, 0.15, 0.6, 0.15, PALETTE.stack));
        parts.push(box(-2.4 + k * 2.4, 0.6, -2.1 + r * 1.4, 2.0, 0.08, 1.0, 0x23407a));
      }
      break;
    }
    case "studio": { // sound stage hangar with a light rig
      parts.push(box(0, 0, 0, 6, 3.2, 4.2, c[1]));
      parts.push(box(0, 3.2, 0, 6.2, 0.4, 4.4, c[0]));
      parts.push(box(0, 0, 2.15, 2.4, 2.4, 0.1, PALETTE.roof));
      for (let i = 0; i < 3; i++) parts.push(cyl(-2 + i * 2, 3.6, -1.4, 0.3, 0.5, PALETTE.bank));
      break;
    }
    case "port": { // quay with stacked containers
      parts.push(box(0, 0, 0, 7, 0.4, 4.6, PALETTE.roof));
      for (let i = 0; i < 4; i++) for (let j = 0; j < 2; j++) for (let h = 0; h < 1 + ((i + j) % 3); h++) {
        parts.push(box(-2.6 + i * 1.6, 0.4 + h * 0.6, -1 + j * 1.4, 1.4, 0.58, 0.6, [0xc2410c, 0x2563eb, 0x16a34a, 0xca8a04][(i + j + h) % 4]));
      }
      break;
    }
    case "office": {
      parts.push(box(0, 0, 0, 3.6, H, 3.0, c[0]));
      for (let fy = 1.4; fy < H - 0.3; fy += 1.8) parts.push(box(0, fy, 0, 3.66, 0.14, 3.06, c[1]));
      parts.push(box(0, H, 0, 2.4, 0.4, 2.0, PALETTE.roof));
      break;
    }
    case "bank": {
      parts.push(box(0, 0, 0, 4.4, 0.5, 3.6, c[1])); // steps
      for (let i = 0; i < 5; i++) parts.push(cyl(-1.8 + i * 0.9, 0.5, 1.4, 0.3, 2.6, PALETTE.civic));
      parts.push(box(0, 0.5, -0.3, 4.0, 2.6, 2.6, c[0]));
      parts.push(box(0, 3.1, 0, 4.6, 0.6, 3.8, PALETTE.bank)); // pediment
      parts.push(box(0, 3.7, -0.6, 2.6, H * 0.7, 2.2, PALETTE.civicDark)); // tower behind
      break;
    }
    case "government": {
      parts.push(box(0, 0, 0, 6, 2.4, 3.4, c[0]));
      parts.push(cyl(0, 2.4, 0, 2.0, 0.8, c[1]));
      parts.push(cyl(0, 3.2, 0, 1.6, 1.4, c[0])); // dome drum
      parts.push(cyl(0, 4.6, 0, 0.12, 1.4, PALETTE.stack)); // flagpole
      break;
    }
    case "exchange": {
      parts.push(box(0, 0, 0, 5, 3.2, 3.4, c[0]));
      for (let i = 0; i < 6; i++) parts.push(cyl(-2.1 + i * 0.84, 0, 1.75, 0.28, 3.2, PALETTE.civic));
      parts.push(box(0, 3.2, 0, 5.4, 0.5, 3.8, c[1]));
      break;
    }
    default: { // plain filler block
      const w = 2.2 + rng() * 1.6, d = 2.2 + rng() * 1.6;
      parts.push(box(0, 0, 0, w, H, d, rng() > 0.5 ? c[0] : c[1]));
      if (H > 6 && rng() > 0.4) parts.push(box(0, H, 0, w * 0.6, 0.8, d * 0.6, c[1]));
    }
  }
  return parts;
}

/** The industry's landmark (industry_kits.HERO_KINDS), as parts around (0, 0). */
function heroProp(kind, rng) {
  const p = [];
  const steel = PALETTE.stack, white = PALETTE.civic, accent = PALETTE.you;
  switch (kind) {
    case "rocket":
      p.push(box(0, 0, 0, 4, 0.5, 4, PALETTE.roof));
      p.push(cyl(0, 0.5, 0, 1.0, 9, white), cyl(0, 9.5, 0, 0.75, 1.2, white), cyl(0, 10.7, 0, 0.4, 1.0, white), cyl(0, 11.7, 0, 0.12, 0.8, accent));
      for (const [x, z] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) p.push(box(x * 0.9, 0.5, z * 0.9, x ? 0.6 : 0.15, 1.6, z ? 0.6 : 0.15, steel));
      p.push(box(2.2, 0, 0, 0.6, 11, 0.6, PALETTE.bank)); // service tower
      break;
    case "dish":
      p.push(box(0, 0, 0, 0.5, 12, 0.5, steel));
      for (let y = 3; y < 12; y += 3) p.push(box(0, y, 0, 2.2, 0.15, 0.15, steel), box(0, y, 0, 0.15, 0.15, 2.2, steel));
      for (const [x, z] of [[1.1, 0], [-1.1, 0], [0, 1.1]]) p.push(box(x, 10, z, 0.3, 1.2, 0.3, white));
      break;
    case "wind":
      for (const [x, z] of [[0, 0], [5, 2.5], [-5, 2.5]]) {
        p.push(cyl(x, 0, z, 0.45, 9, white), box(x, 9, z + 0.3, 0.6, 0.6, 1.0, white));
        for (let b = 0; b < 3; b++) {
          const a = b * 2.094 + rng();
          p.push(box(x + Math.cos(a) * 1.9, 9.3 + Math.sin(a) * 1.9, z + 0.85, Math.abs(Math.cos(a)) * 3.6 + 0.25, Math.abs(Math.sin(a)) * 3.6 + 0.25, 0.12, white));
        }
      }
      break;
    case "derrick":
      p.push(box(0, 0, 0, 4, 0.6, 4, PALETTE.roof));
      for (let y = 0.6; y < 11; y += 1.4) { const w = 3 - (y / 11) * 2.4; p.push(box(0, y, 0, w, 0.15, w, PALETTE.bank)); }
      for (const [x, z] of [[1, 1], [-1, 1], [1, -1], [-1, -1]]) p.push(box(x * 0.8, 0.6, z * 0.8, 0.2, 10.5, 0.2, PALETTE.bank));
      p.push(cyl(2.6, 0, 1.6, 0.9, 1.4, white));
      break;
    case "crane":
      p.push(box(0, 0, 0, 0.8, 13, 0.8, PALETTE.bank));
      p.push(box(3, 13, 0, 10, 0.5, 0.6, PALETTE.bank), box(-1.8, 13.5, 0, 2, 0.8, 0.8, steel));
      p.push(box(6.5, 10.5, 0, 0.08, 2.5, 0.08, steel), box(6.5, 10.1, 0, 1.2, 0.4, 0.9, accent));
      break;
    case "silo":
      for (let i = 0; i < 4; i++) p.push(cyl(-2.4 + i * 1.6, 0, 0, 1.4, 6 + (i % 2), white), cyl(-2.4 + i * 1.6, 6 + (i % 2), 0, 1.0, 0.6, steel));
      p.push(box(0, 6.6, 0, 6, 0.25, 0.4, steel));
      break;
    case "headframe":
      p.push(box(0, 0, 0, 0.5, 9, 0.5, steel), box(2.4, 0, 0, 0.5, 9, 0.5, steel), box(1.2, 9, 0, 3.2, 0.6, 0.8, PALETTE.bank));
      p.push(cyl(1.2, 9.6, 0, 1.6, 0.25, steel), box(-2.2, 0, 1.5, 3, 2, 2.4, 0x8a7a63));
      break;
    case "dome":
      p.push(box(0, 0, 0, 6, 2.4, 6, white), cyl(0, 2.4, 0, 5, 1.0, white), cyl(0, 3.4, 0, 4, 1.0, white), cyl(0, 4.4, 0, 2.6, 0.9, white), cyl(0, 5.3, 0, 0.8, 0.8, accent));
      break;
    case "stack":
      p.push(box(0, 0, 0, 5, 2.4, 3.6, white));
      for (let i = 0; i < 3; i++) p.push(cyl(-1.6 + i * 1.6, 2.4, -0.6, 0.7, 7 + i * 1.2, steel), cyl(-1.6 + i * 1.6, 9.4 + i * 1.2, -0.6, 0.8, 0.3, 0xfb7185));
      break;
    case "billboard":
      p.push(box(0, 0, 0, 0.4, 7, 0.4, steel), box(0, 7, 0, 7, 3.4, 0.3, accent), box(0, 7.2, 0.18, 6.4, 3, 0.05, 0x1e3a5f));
      break;
    case "ferris":
      p.push(box(-1.4, 0, 0, 0.3, 7, 0.3, steel), box(1.4, 0, 0, 0.3, 7, 0.3, steel));
      for (let k = 0; k < 12; k++) { const a = (k / 12) * Math.PI * 2; p.push(box(Math.cos(a) * 5, 7 + Math.sin(a) * 5, 0, 0.8, 0.7, 0.8, [0xfb7185, 0x7ee8fa, 0xfbbf24][k % 3])); }
      p.push(cyl(0, 6.6, 0, 0.6, 0.8, steel));
      break;
    default: // "tower"
      return building("tower", 34, "you", rng);
  }
  return p;
}

const translate = (parts, dx, dz, extra) => parts.map((p) => ({ ...p, x: p.x + dx, z: p.z + dz, ...extra }));

/** Site building kind per world group (backend world_view.WORLD_GROUPS). */
const SITE_KIND = { digital: "datacenter", industrial: "factory", consumer: "store", resources: "rig", services: "office", care: "campus" };

/**
 * The home district: HQ at the centre, your sites, rivals, the bank, the
 * government and the exchange, then scenery. Returns { parts, labels, size }.
 * labels: [{ text, sub, kind, x, y, z, ref }] for the HTML overlay and picking.
 */
/**
 * Which ghost building (if any) a forecast decision adds, and in which
 * market: { market, kind, label, sub } or null. `r` is the /api/forecast result.
 */
function ghostFor(action, r, hqCountry) {
  if (!action || !r) return null;
  const p = action.params || {};
  const d = (r.deltas && r.deltas.net_income) || 0;
  const sub = `forecast · ${d >= 0 ? "+" : "-"}$${Math.abs(Math.round(d / 1000)).toLocaleString("en-US")}K profit in ${r.horizon_months} mo · not built`;
  switch (action.type) {
    case "build_factory": return { market: hqCountry, kind: "site", label: `Planned site: ${p.product_name || "new product"}`, sub };
    case "launch_product": return { market: hqCountry, kind: "site", label: `Planned launch: ${p.name || "new product"}`, sub };
    case "expand_market": return { market: p.country_name || hqCountry, kind: "site", label: `Planned expansion${p.country_name ? `: ${p.country_name}` : ""}`, sub };
    case "acquire_company": return { market: hqCountry, kind: "tower", label: `${p.target_name || "Target"}, if you buy it`, sub };
    default: return null;
  }
}

function district(world, seedText = "district") {
  const rng = rngFrom(seedText);
  const parts = [], labels = [];
  const S = 9; // lot spacing
  const lots = [];
  for (let gx = -4; gx <= 4; gx++) for (let gz = -4; gz <= 4; gz++) lots.push([gx, gz]);
  lots.sort((a, b) => Math.hypot(...a) - Math.hypot(...b) || a[0] - b[0] || a[1] - b[1]);
  const taken = new Set();
  const take = (pred) => {
    const lot = lots.find((l) => !taken.has(l.join()) && (!pred || pred(l)));
    if (!lot) return null;
    taken.add(lot.join());
    return [lot[0] * S, lot[1] * S];
  };

  // ground, roads, plaza
  parts.push(box(0, -0.2, 0, S * 9 + 6, 0.2, S * 9 + 6, PALETTE.ground));
  for (let g = -4; g <= 5; g++) {
    parts.push(box((g - 0.5) * S, -0.05, 0, 1.6, 0.05, S * 9 + 6, PALETTE.road));
    parts.push(box(0, -0.05, (g - 0.5) * S, S * 9 + 6, 0.05, 1.6, PALETTE.road));
  }
  parts.push(box(0, -0.02, 0, S - 2, 0.04, S - 2, PALETTE.plaza));

  const sites = world.sites || {};
  const company = world.company || {};
  // HQ
  const floors = hqFloors(sites.headcount);
  take((l) => l[0] === 0 && l[1] === 0);
  parts.push(...translate(building("tower", floors, "you", rng), 0, 0, { ref: "hq" }));
  labels.push({ text: company.name || "HQ", sub: `HQ · ${sites.headcount || 0} people · ${floors} floors`, kind: "hq", x: 0, y: floors * FLOOR + 3, z: 0, ref: "hq" });

  // your sites: the industry's own site building (industry_kits), else the group's
  const kit = world.kit || {};
  const kind = (kit.site && kit.site.kind) || SITE_KIND[world.group] || "office";
  const siteAt = [];
  const factories = sites.factories || [];
  const siteList = factories.length
    ? factories.map((f) => ({ text: f.name, sub: `${Math.round(f.utilization_pct || 0)}% utilised · ${f.status}`, hot: (f.utilization_pct || 0) > 90 }))
    : Array.from({ length: Math.max(1, sites.products || 0) }, (_, i) => ({ text: `${(kit.site && kit.site.label) || "Site"} ${i + 1}`, sub: "runs your products" }));
  siteList.slice(0, 12).forEach((s, i) => {
    const at = take((l) => Math.hypot(...l) >= 1 && Math.hypot(...l) <= 2.3);
    if (!at) return;
    const p = translate(building(kind, 3, "you", rng), at[0], at[1], { ref: `site:${i}`, glow: s.hot ? 1 : 0 });
    parts.push(...p);
    labels.push({ text: s.text, sub: s.sub, kind: "site", x: at[0], y: 6, z: at[1], ref: `site:${i}`, hot: !!s.hot });
    siteAt.push({ x: at[0], z: at[1], ref: `site:${i}`, hot: !!s.hot });
  });

  // Ghost Futures (Phase 7): the building a forecast decision would add,
  // drawn translucent and labelled as a forecast; it is not built
  if (world.ghost) {
    const g = world.ghost;
    const at = take((l) => Math.hypot(...l) >= 1 && Math.hypot(...l) <= 2.6);
    if (at) {
      parts.push(...translate(building(g.kind === "tower" ? "tower" : kind, g.kind === "tower" ? 8 : 3, "you", rng), at[0], at[1], { ref: "ghost", ghost: 1 }));
      labels.push({ text: `👻 ${g.label}`, sub: g.sub, kind: "ghost", x: at[0], y: g.kind === "tower" ? 8 * FLOOR + 3 : 6, z: at[1], ref: "ghost" });
    }
  }

  // the industry's landmark
  if (kit.hero) {
    const at = take((l) => Math.hypot(...l) >= 1.4 && Math.hypot(...l) <= 2.9);
    if (at) {
      parts.push(...translate(heroProp(kit.hero.kind, rng), at[0], at[1], { ref: "hero" }));
      labels.push({ text: kit.hero.label, sub: "your industry", kind: "hero", x: at[0], y: 14, z: at[1], ref: "hero" });
    }
  }

  // supplier depots on the edge, one per real supply input
  const inputs = ((world.supply && world.supply.inputs) || []).slice(0, 4);
  const depots = [];
  inputs.forEach((inp, i) => {
    const at = take((l) => Math.max(Math.abs(l[0]), Math.abs(l[1])) === 4 && (i % 2 ? l[0] === -4 : l[1] === -4));
    if (!at) return;
    const short = inp.shortage_pct > 0;
    parts.push(...translate(building("warehouse", 2, "civic", rng), at[0], at[1], { ref: `input:${inp.label}`, glow: short ? 1 : 0 }));
    labels.push({ text: inp.label, sub: `${inp.strategy || "supplier"}${short ? ` · ${inp.shortage_pct}% short` : ""}`, kind: "depot", x: at[0], y: 5, z: at[1], ref: `input:${inp.label}`, hot: short });
    depots.push({ x: at[0], z: at[1], ref: `input:${inp.label}`, blocked: short });
  });

  // civic buildings
  const civic = [["bank", "Bank district", "Your lenders"], ["government", "Government", `${company.hq_country || ""} ministries`], ["exchange", "Stock exchange", "Public markets"]];
  civic.forEach(([k, text, sub], i) => {
    const at = take((l) => Math.hypot(...l) >= 2 && Math.hypot(...l) <= 3.2);
    if (!at) return;
    parts.push(...translate(building(k, 14, "civic", rng), at[0], at[1], { ref: k }));
    labels.push({ text, sub, kind: k, x: at[0], y: 9, z: at[1], ref: k });
  });

  // rivals: tower height = market share
  const rivals = (world.rivals || []).slice(0, 10);
  const maxShare = Math.max(...rivals.map((r) => r.market_share_pct || 0), 1e-6);
  rivals.forEach((r, i) => {
    const at = take((l) => Math.hypot(...l) >= 2.2 && Math.hypot(...l) <= 4);
    if (!at) return;
    const f = rivalFloors(r.market_share_pct, maxShare);
    parts.push(...translate(building("tower", f, "rival", rng), at[0], at[1], { ref: `rival:${i}` }));
    labels.push({ text: r.name, sub: `${(r.market_share_pct || 0).toFixed(1)}% share · ${r.strategy}`, kind: "rival", x: at[0], y: f * FLOOR + 3, z: at[1], ref: `rival:${i}` });
  });

  // scenery: filler blocks on every lot left, taller toward the centre
  for (const l of lots) {
    if (taken.has(l.join())) continue;
    const dist = Math.hypot(...l);
    for (let k = 0; k < 2; k++) {
      const f = Math.max(2, Math.round((10 - dist * 1.6) * (0.4 + rng() * 0.9)));
      const ox = (k ? 1 : -1) * 2.2, oz = (rng() - 0.5) * 3;
      parts.push(...translate(building("block", f, "filler", rng), l[0] * S + ox, l[1] * S + oz, { ref: "scenery" }));
    }
    if (rng() > 0.6) parts.push(cyl(l[0] * S, 0, l[1] * S + 3.4, 0.9, 1.3, PALETTE.green, { ref: "scenery" }));
  }
  return { parts, labels, size: S * 9 + 6, routes: routesFor(world, kit, siteAt, depots, S) };
}

/**
 * What visibly moves in the district (industry_kits flow kind):
 *   goods (trucks/rail/ships/pipeline): supplier depots -> your sites -> the city edge
 *   people: from the city edge to your sites and HQ
 *   data: arcs between your sites, HQ and the edge (users)
 * Roads run between the lots, so ground routes follow the grid. A route into
 * a bottleneck (a site over 90% or a short supply input) is `blocked`: its
 * vehicles queue at the end and turn red.
 */
function routesFor(world, kit, siteAt, depots, S) {
  const flow = (kit.flow && kit.flow.kind) || "trucks";
  const hq = { x: 0, z: 0, ref: "hq", hot: false };
  const targets = siteAt.length ? siteAt : [hq];
  const road = (v) => (Math.round(v / S - 0.5) + 0.5) * S;
  const grid = (a, b) => [[a.x, a.z], [road(a.x), a.z], [road(a.x), road(b.z)], [b.x, road(b.z)], [b.x, b.z]];
  const hqRevenue = ((world.countries || []).find((c) => c.status === "hq") || {}).estimated_revenue || 0;
  const count = Math.max(2, Math.min(8, Math.round(2 + Math.log10(1 + hqRevenue / 2000) * 1.5)));
  const edge = [[4.5 * S, 0], [0, 4.5 * S], [-4.5 * S, 0]];
  const out = [];
  if (flow === "data") {
    targets.forEach((t, i) => {
      out.push({ kind: "data", air: true, points: [[t.x, t.z], [0, 0]], count, blocked: t.hot, to: t.ref });
      const [ex, ez] = edge[i % edge.length];
      out.push({ kind: "data", air: true, points: [[t.x, t.z], [ex, ez]], count, blocked: false, to: "edge" });
    });
    return out;
  }
  if (flow === "people") {
    [...targets, hq].forEach((t, i) => {
      const [ex, ez] = edge[i % edge.length];
      out.push({ kind: "people", points: grid({ x: ex, z: ez }, t), count: count + 2, blocked: t.hot, to: t.ref });
    });
    return out;
  }
  depots.forEach((d, i) => {
    const t = targets[i % targets.length];
    out.push({ kind: flow, points: grid(d, t), count, blocked: d.blocked || t.hot, to: t.ref, from: d.ref });
  });
  targets.forEach((t, i) => {
    const [ex, ez] = edge[i % edge.length];
    out.push({ kind: flow, points: grid(t, { x: ex, z: ez }), count, blocked: false, to: "market", from: t.ref });
  });
  return out;
}

const MAX_DESKS = 1200;
const DEPT_COLORS = [0x7ee8fa, 0xa78bfa, 0x5eead4, 0xfbbf24, 0xfb7185, 0x34d399, 0x9fb4ff, 0xf6b26b];

/**
 * Inside HQ: one zone per department, one desk (and person) per employee,
 * or one per `perDesk` people when there are more than MAX_DESKS. Corner
 * offices for executives. Returns { parts, labels, perDesk, size }.
 */
/**
 * One work station of an industry floor (industry_kits.INTERIOR_KINDS) at
 * (x, z), plus the person working it. `state` is "idle" | "ok" | "hot":
 * the real utilization of your capacity (dim when idle, red when over).
 */
function station(kind, x, z, color, state, ref) {
  const tint = state === "hot" ? 0xff4d6d : state === "idle" ? 0x2a3150 : null;
  const extra = { ref, glow: state === "hot" ? 1 : 0 };
  const person = cyl(x, kind === "ward" ? 0 : 0.45, z + 0.55, 0.3, 0.75, color, { ref });
  switch (kind) {
    case "line": return [box(x, 0, z, 1.6, 0.35, 0.6, 0x39415e, { ref }), box(x, 0.35, z - 0.05, 0.7, 0.7, 0.5, tint || 0x5a6a9a, extra), person];
    case "floor": return [box(x, 0, z, 1.3, 1.3, 0.4, tint || 0x8a6f4d, extra), cyl(x + 0.5, 0, z + 0.7, 0.3, 0.75, 0xd8d0bd, { ref }), person];
    case "ward": return [box(x, 0, z, 0.9, 0.45, 1.6, tint || 0xe2e8f0, extra), box(x, 0.45, z - 0.6, 0.9, 0.12, 0.35, 0xffffff, { ref }), cyl(x + 0.75, 0, z, 0.3, 0.75, color, { ref })];
    case "lab": return [box(x, 0, z, 1.4, 0.9, 0.6, tint || 0xcbd5e1, extra), box(x, 0.9, z - 0.2, 0.5, 0.35, 0.2, 0x7ee8fa, { ref }), person];
    case "field": return [box(x, 0, z, 1.4, 0.25, 0.5, tint || 0x3f8a52, extra), box(x, 0.25, z, 1.2, 0.35, 0.3, tint || 0x5aa35f, { ref }), person];
    case "control": return [box(x, 0, z, 1.2, 0.7, 0.5, 0x232a48, { ref }), box(x, 0.7, z - 0.18, 1.0, 0.45, 0.06, tint || 0x7ee8fa, extra), person];
    case "stage": return [box(x, 0, z, 0.25, 1.6, 0.25, 0x39415e, { ref }), box(x, 1.5, z, 0.5, 0.35, 0.6, tint || 0x232a48, extra), person];
    default: return [box(x, 0, z, 1.0, 0.45, 0.6, 0x2a3150, { ref }), person];
  }
}

const FLOOR_DEPT = /operations|production|manufactur|delivery|service|clinical|care|field|store|network/i;

/**
 * Inside HQ. One zone per department, one desk (or station) per person, or
 * one per `perDesk` above MAX_DESKS. The team that runs operations works
 * on the industry's own floor (`kind`); everyone else keeps a desk. Corner
 * offices for executives.
 *   opts.utilization  your capacity utilization %, if the engine has one
 * Returns { parts, labels, perDesk, solo, size, belts, floorZone, state }.
 * belts: rows of a production line, for the moving-items animation.
 */
function interior(sites, kind = "office", opts = {}) {
  let depts = (sites.departments || []).filter((d) => d.headcount > 0);
  const solo = !depts.length; // a solo founder: no staff yet, just your own desk
  if (solo) depts = [{ department: "You, the founder", headcount: 1 }];
  const total = depts.reduce((s, d) => s + d.headcount, 0);
  const perDesk = Math.max(1, Math.ceil(total / MAX_DESKS));
  const parts = [], labels = [];
  const cols = Math.max(1, Math.ceil(Math.sqrt(depts.length || 1)));
  const zones = depts.map((d) => ({ ...d, desks: Math.ceil(d.headcount / perDesk) }));
  const zoneSide = Math.max(8, Math.ceil(Math.sqrt(Math.max(...zones.map((z) => z.desks), 1))) * 1.6 + 3);
  const rows = Math.ceil(zones.length / cols) || 1;
  const W = cols * (zoneSide + 2), D = rows * (zoneSide + 2);
  parts.push(box(0, -0.3, 0, W + 6, 0.3, D + 6, PALETTE.ground));
  // cutaway walls: back and left only, so the camera sees in
  parts.push(box(0, 0, -D / 2 - 2.6, W + 6, 1.6, 0.3, PALETTE.filler));
  parts.push(box(-W / 2 - 2.6, 0, 0, 0.3, 1.6, D + 6, PALETTE.filler));

  // which zone is the industry floor: an operations-like department, else the biggest
  let floorIdx = -1;
  if (kind !== "office" && !solo) {
    floorIdx = zones.findIndex((z) => FLOOR_DEPT.test(z.department));
    if (floorIdx < 0) floorIdx = zones.reduce((best, z, i) => (z.desks > zones[best].desks ? i : best), 0);
  }
  const u = opts.utilization;
  const state = u == null ? "ok" : u > 100 ? "hot" : u < 40 ? "idle" : "ok";
  const belts = [];

  zones.forEach((z, i) => {
    const cx = -W / 2 + (i % cols) * (zoneSide + 2) + zoneSide / 2 + 1;
    const cz = -D / 2 + Math.floor(i / cols) * (zoneSide + 2) + zoneSide / 2 + 1;
    const color = DEPT_COLORS[i % DEPT_COLORS.length];
    parts.push(box(cx, -0.02, cz, zoneSide, 0.04, zoneSide, PALETTE.plaza, { ref: `dept:${i}` }));
    const per = Math.max(1, Math.ceil(Math.sqrt(z.desks)));
    const onFloor = i === floorIdx;
    for (let k = 0; k < z.desks; k++) {
      const dx = cx - (per - 1) * 0.8 + (k % per) * 1.6;
      const dz = cz - (per - 1) * 0.8 + Math.floor(k / per) * 1.6;
      if (onFloor) parts.push(...station(kind, dx, dz, color, state, `dept:${i}`));
      else {
        parts.push(box(dx, 0, dz, 1.0, 0.45, 0.6, 0x2a3150, { ref: `dept:${i}` }));
        parts.push(cyl(dx, 0.45, dz + 0.45, 0.32, 0.75, color, { ref: `dept:${i}` }));
      }
    }
    if (onFloor && kind === "line") {
      for (let r = 0; r < Math.ceil(z.desks / per); r++) {
        const zRow = cz - (per - 1) * 0.8 + r * 1.6;
        belts.push({ x0: cx - (per - 1) * 0.8 - 0.8, x1: cx + (per - 1) * 0.8 + 0.8, y: 0.42, z: zRow, state });
      }
    }
    const floorNote = i === floorIdx ? ` · ${opts.floorLabel || "the floor"}${u != null ? ` at ${Math.round(u)}%` : ""}` : "";
    labels.push({ text: z.department, sub: solo ? "No staff yet" : `${z.headcount} people${perDesk > 1 ? ` · 1 desk = ${perDesk}` : ""}${floorNote}`, kind: i === floorIdx ? "floor" : "dept", x: cx, y: 3, z: cz, ref: `dept:${i}`, hot: i === floorIdx && state === "hot" });
  });

  const execs = Math.min(sites.executives || 0, 8);
  for (let e = 0; e < execs; e++) {
    const x = W / 2 + 1.2, z = -D / 2 + 1 + e * 2.6;
    parts.push(box(x, 0, z, 2.2, 2.4, 2.2, PALETTE.glass, { ref: "execs" }));
    parts.push(cyl(x, 0, z, 0.32, 0.75, PALETTE.bank, { ref: "execs" }));
  }
  if (execs) labels.push({ text: "Executive offices", sub: `${sites.executives} executives`, kind: "execs", x: W / 2 + 1.2, y: 4, z: -D / 2 + 1, ref: "execs" });
  return { parts, labels, perDesk, solo, size: Math.max(W, D) + 6, belts, floorZone: floorIdx, state };
}

export { FLOOR, MAX_DESKS, PALETTE, SITE_KIND, building, district, ghostFor, heroProp, hqFloors, interior, rivalFloors, rngFrom, routesFor, station };
